import React, { useState, useEffect } from 'react';
import sharepointService from '../services/sharepointService';

/**
 * Modal component for creating a new SharePoint folder
 * @param {boolean} isOpen - Whether the modal is open
 * @param {function} onClose - Function to close the modal
 * @param {string} clientName - The client name
 * @param {string} parentFolderPath - The parent folder path where the new folder will be created
 * @param {function} onFolderCreated - Callback function when folder is created successfully
 */
export default function CreateFolderModal({
    isOpen,
    onClose,
    clientName,
    parentFolderPath,
    onFolderCreated
}) {
    const [folderName, setFolderName] = useState('');
    const [isCreating, setIsCreating] = useState(false);
    const [error, setError] = useState('');

    // Reset state when modal opens/closes
    useEffect(() => {
        if (isOpen) {
            setFolderName('');
            setError('');
            setIsCreating(false);
        }
    }, [isOpen]);

    const handleCreateFolder = async () => {
        const trimmedFolderName = folderName.trim();
        
        if (!trimmedFolderName) {
            setError('Folder name is required');
            return;
        }

        // Basic validation for folder name
        const invalidChars = ['<', '>', ':', '"', '/', '\\', '|', '?', '*'];
        const hasInvalidChars = invalidChars.some(char => trimmedFolderName.includes(char));
        
        if (hasInvalidChars) {
            setError(`Folder name cannot contain the following characters: ${invalidChars.join(' ')}`);
            return;
        }

        setIsCreating(true);
        setError('');

        try {
            const response = await sharepointService.createFolder(
                trimmedFolderName,
                parentFolderPath || '',
                clientName
            );

            if (response.success) {
                // Notify parent component about the successful folder creation
                if (onFolderCreated) {
                    onFolderCreated(response.folder);
                }
                onClose();
            } else {
                // Handle specific error types
                if (response.error === 'FOLDER_EXISTS') {
                    setError(`A folder named "${trimmedFolderName}" already exists in this location.`);
                } else {
                    setError(response.message || response.error || 'Failed to create folder');
                }
            }
        } catch (err) {
            console.error('Error creating folder:', err);
            
            // Parse error response
            let errorMessage = 'Failed to create folder';
            if (err.response?.data) {
                const errorData = err.response.data;
                if (typeof errorData === 'string') {
                    errorMessage = errorData;
                } else if (errorData.detail) {
                    errorMessage = errorData.detail;
                } else if (errorData.message) {
                    errorMessage = errorData.message;
                } else if (errorData.error) {
                    errorMessage = errorData.error;
                }
            } else if (err.message) {
                errorMessage = err.message;
            }
            
            setError(errorMessage);
        } finally {
            setIsCreating(false);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        handleCreateFolder();
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleCreateFolder();
        }
    };

    if (!isOpen) return null;

    return (
        <>
            <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1" role="dialog">
                <div className="modal-dialog" role="document">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">
                                <i className="bi bi-folder-plus me-2 text-primary"></i>
                                Create New Folder
                            </h5>
                            <button
                                type="button"
                                className="btn-close"
                                onClick={onClose}
                                disabled={isCreating}
                            ></button>
                        </div>

                        <form onSubmit={handleSubmit}>
                            <div className="modal-body">
                                <div className="alert alert-info">
                                    <strong>Creating folder for client "{clientName}"</strong>
                                    {parentFolderPath && (
                                        <div className="mt-1">
                                            <small>Location: {parentFolderPath}</small>
                                        </div>
                                    )}
                                </div>

                                {error && (
                                    <div className="alert alert-danger">
                                        <i className="bi bi-exclamation-triangle me-2"></i>
                                        {error}
                                    </div>
                                )}

                                <div className="mb-3">
                                    <label htmlFor="folderName" className="form-label">
                                        <strong>Folder Name:</strong>
                                    </label>
                                    <input
                                        type="text"
                                        id="folderName"
                                        className="form-control"
                                        value={folderName}
                                        onChange={(e) => {
                                            setFolderName(e.target.value);
                                            setError(''); // Clear error when user starts typing
                                        }}
                                        onKeyPress={handleKeyPress}
                                        placeholder="Enter folder name..."
                                        disabled={isCreating}
                                        autoFocus
                                        maxLength={255}
                                    />
                                    <div className="form-text">
                                        Folder name cannot contain: &lt; &gt; : " / \ | ? *
                                    </div>
                                </div>

                                <div className="alert alert-secondary">
                                    <i className="bi bi-info-circle me-2"></i>
                                    The new folder will be created in SharePoint and will be available for file uploads.
                                </div>
                            </div>

                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={onClose}
                                    disabled={isCreating}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    disabled={!folderName.trim() || isCreating}
                                >
                                    {isCreating ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                            Creating...
                                        </>
                                    ) : (
                                        <>
                                            <i className="bi bi-folder-plus me-2"></i>
                                            Create Folder
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
            <div className="modal-backdrop fade show"></div>
        </>
    );
}
